import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const NAV = [
  { path: '/',            label: 'Overview',     icon: '⬛' },
  { path: '/operations',  label: 'Operations',   icon: '🔧' },
  { path: '/performance', label: 'Performance',  icon: '⚡' },
  { path: '/storage',     label: 'Storage',      icon: '💾' },
  { path: '/analytics',   label: 'Analytics',    icon: '📊' },
  { path: '/restore',     label: 'Restore',      icon: '♻️' },
  { path: '/settings',    label: 'Impostazioni', icon: '⚙️' },
];

// Determina se un colore di sfondo richiede testo chiaro o scuro per
// restare leggibile (formula di luminanza relativa, soglia 0.55 — leggermente
// più permissiva del classico 0.5 dato che la sidebar usa font non in grassetto
// e contrasti già piuttosto netti).
function isLightColor(hex) {
  if (!hex) return false;
  const c = hex.replace('#', '');
  if (c.length !== 6) return false;
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.55;
}

export default function Layout({ children, customer, customers, onCustomerChange }) {
  const { user, logout } = useAuth();
  const { theme }        = useTheme();
  const navigate         = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const sidebarW = collapsed ? 64 : 220;

  // Testi/bordi della sidebar si adattano automaticamente in base a quanto
  // è chiaro o scuro il colore di sfondo scelto, per restare sempre leggibili.
  const sidebarIsLight = isLightColor(theme.sidebarColor);
  const sidebarTextColor      = sidebarIsLight ? '#1e293b' : '#fff';
  const sidebarMutedTextColor = sidebarIsLight ? '#64748b' : '#94a3b8';
  const sidebarBorderColor    = sidebarIsLight ? '#e2e8f0' : '#334155';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', background: '#f8fafc' }}>
      {/* Sidebar */}
      <aside style={{
        width: sidebarW, minHeight: '100vh', flexShrink: 0,
        background: theme.sidebarColor, color: sidebarTextColor,
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.2s', overflow: 'hidden',
      }}>
        {/* Logo */}
        <div style={{ padding: collapsed ? '20px 12px' : '20px 20px', display: 'flex', alignItems: collapsed ? 'center' : 'flex-start', gap: 12, borderBottom: `1px solid ${sidebarBorderColor}` }}>
          {theme.logoUrl
            ? <img src={theme.logoUrl} alt="logo" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 8, flexShrink: 0 }} />
            : <div style={{ width: 36, height: 36, borderRadius: 8, background: theme.primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, flexShrink: 0, color: '#fff' }}>
                {theme.companyName.charAt(0)}
              </div>
          }
          {!collapsed && <span style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'visible' }}>{theme.companyName}</span>}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 0' }}>
          {NAV.map(({ path, label, icon }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 12,
                padding: collapsed ? '12px 18px' : '12px 20px',
                color: isActive ? '#fff' : sidebarMutedTextColor,
                background: isActive ? theme.primaryColor : 'transparent',
                textDecoration: 'none', fontSize: 14, fontWeight: 500,
                borderRadius: 8, margin: '2px 8px',
                transition: 'all 0.15s',
              })}
              title={collapsed ? label : ''}
            >
              <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
              {!collapsed && <span style={{ whiteSpace: 'nowrap' }}>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User / logout */}
        <div style={{ padding: collapsed ? '12px 8px' : '12px 16px', borderTop: `1px solid ${sidebarBorderColor}` }}>
          {!collapsed && (
            <div style={{ fontSize: 12, color: sidebarMutedTextColor, marginBottom: 8, padding: '0 4px' }}>
              {user?.username || 'utente'}
            </div>
          )}
          <button
            onClick={handleLogout}
            title="Logout"
            style={{
              width: '100%', padding: '8px 12px', background: '#ef4444',
              border: 'none', borderRadius: 8, color: '#fff',
              cursor: 'pointer', fontSize: 13, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <span>🚪</span>
            {!collapsed && 'Logout'}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Topbar */}
        <header style={{
          height: 56, background: '#fff', borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', padding: '0 24px', gap: 16,
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <button
            onClick={() => setCollapsed(c => !c)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#64748b' }}
          >
            ☰
          </button>

          {/* Filtro cliente */}
          <select
            value={customer || ''}
            onChange={e => onCustomerChange(e.target.value || null)}
            style={{
              padding: '6px 12px', borderRadius: 8, border: '1.5px solid #d1d5db',
              fontSize: 14, color: '#1e293b', background: '#fff', cursor: 'pointer',
            }}
          >
            <option value="">Tutti i clienti</option>
            {customers.map(c => (
              <option key={c.customer_code} value={c.customer_code}>
                {c.customer_name} ({c.customer_code})
              </option>
            ))}
          </select>

          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
            🕐 {new Date().toLocaleString('it-IT')}
          </div>
        </header>

        {/* Content */}
        <main style={{ flex: 1, padding: 24, minWidth: 0 }}>
          {children}
        </main>
      </div>
    </div>
  );
}
